import { SEVERITY, severityToHealth, normalizeCategoryCode } from "./constants";

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Coral stores alert timestamps as UNIX seconds, but the UI works in millis.
// Treat a 10-digit (seconds) value as seconds -> ms; pass an already-millis value through.
const toEpochMs = (v) => {
  const n = toNum(v, 0);
  if (!n) return 0;
  return n < 1e12 ? Math.round(n * 1000) : n;
};

export const normalizeAlert = (a = {}) => {
  const severity = toNum(pick(a, "severity"), SEVERITY.NA);
  const message =
    pick(a, "alertDescription", "alertdescription", "alert_description", "name", "serviceCode") ||
    "—";
  // Alert messages embed the device IP as "Device ID : x.x.x.x" — use it as a
  // fallback when the alert row's ipaddress column isn't populated.
  const ipFromMsg = (String(message).match(/Device ID\s*:\s*([\d.]+)/i) || [])[1];
  const ipAddress = pick(a, "ipAddress", "ipaddress", "ip_address") || ipFromMsg;
  return {
    id: pick(a, "id"),
    assetId: pick(a, "assetId", "assetid", "asset_id"),
    uuid: pick(a, "uuid"),
    category: normalizeCategoryCode(
      pick(a, "categoryCode", "categorycode", "category_code", "category")
    ),
    severity,
    health: severityToHealth(severity),
    raisedAt: toEpochMs(pick(a, "alertGeneratedOn", "alertgeneratedon", "alert_generated_on")),
    closedAt: toEpochMs(pick(a, "alertClosedOn", "alertclosedon", "alert_closed_on")),
    message,
    nodeName: pick(a, "nodeName", "nodename", "node_name") || ipAddress || pick(a, "alias"),
    ipAddress,
    location: pick(a, "location"),
    serviceCode: pick(a, "serviceCode", "servicecode", "service_code"),
  };
};

export const normalizeDevice = (d = {}) => {
  const severity = toNum(pick(d, "severity"), SEVERITY.NA);
  return {
    id: pick(d, "id"),
    uuid: pick(d, "uuid"),
    nodeId: pick(d, "nodeId", "nodeid", "node_id"),
    nodeName: pick(d, "nodeName", "nodename", "node_name"),
    category: normalizeCategoryCode(pick(d, "category")),
    severity,
    health: severityToHealth(severity),
    working: pick(d, "status"),
    lastSeen: toNum(
      pick(
        d,
        "lastHelathUpdateOn",
        "last_helath_update_on",
        "lastHealthUpdateOn",
        "severityUpdatedOn",
        "severity_updated_on",
        "lastRegistrationReceivedOn",
        "last_registration_received_on"
      )
    ),
    ipAddress: pick(d, "ipAddress", "ipaddress", "ip_address"),
    location: pick(d, "location"),
    message: pick(d, "deviceMessage", "device_message"),
    xCoordinate: pick(d, "xCoordinate", "xcoordinate"),
    yCoordinate: pick(d, "yCoordinate", "ycoordinate"),
    macAddress: pick(d, "macAddress", "macaddress", "mac_address"),
    serverGroupId: pick(d, "serverGroupId", "servergroupid", "server_group_id"),
  };
};

export const normalizeSummaryRow = (row = {}) => ({
  code: normalizeCategoryCode(pick(row, "category", "categoryCode", "code")),
  total: toNum(pick(row, "count", "total")),
});

export const normalizeLocation = (l = {}) => ({
  id: pick(l, "id"),
  name: pick(l, "name"),
  code: pick(l, "code"),
  parentLocationId: pick(l, "parentLocation")?.id ?? null,
  parentLocationName: pick(l, "parentLocation")?.description ?? null,
  backgroundImageURL: pick(l, "backgroundImageURL"),
});
